import { getApi, postApi, putApi, delApi } from "@/shared/utils/fetchApi";
import { API_CONFIG } from "@/infrastructure/config/api";
import {
  IntegrationStatus,
  IntegrationBase,
  CreateIntegrationBaseData,
  CreateIntegrationBaseResult,
  IntegrationPostInstall,
  UpdateIntegrationBaseData,
  IntegrationClassification,
  IntegrationDependencyError,
} from "../types/integration";

/**
 * Classe de serviço para operações relacionadas às integrações base (integrações configuradas)
 * Implementa o padrão Facade para simplificar as chamadas à API
 */
export class IntegrationBaseService {
  private static readonly BASE_URL = "/integration-bases";

  /**
   * Obtém todas as integrações base com filtros opcionais
   * @param params Parâmetros de filtro
   * @returns Lista de integrações base
   */
  static async getAll(params?: {
    product_id?: string;
    integration_id?: string;
    classification?: IntegrationClassification;
    status?: IntegrationStatus;
    scope?: "local" | "global";
    search?: string;
  }): Promise<IntegrationBase[]> {
    try {
      // Sem `organization_id` nem `with_template` na assinatura: a organização vem
      // do token e o template é buscado à parte. Enquanto estavam aqui, quatro
      // chamadores mandavam `organization_id` e o filtro nunca ia na query —
      // parâmetro aceito e ignorado parece filtro aplicado.
      // product_id → workspace scope: v2 retorna globais + locais vinculadas ao produto
      // (com shadow dedup), parity com o legacy. Forward dos demais filtros que v2 entende.
      const v2Params = new URLSearchParams();
      if (params?.status) v2Params.append("status", params.status);
      if (params?.classification)
        v2Params.append("classification", params.classification);
      if (params?.integration_id)
        v2Params.append("integration_id", params.integration_id);
      if (params?.search) v2Params.append("search", params.search);
      if (params?.scope) v2Params.append("scope", params.scope);
      if (params?.product_id) v2Params.append("product_id", params.product_id);
      const qs = v2Params.toString();
      const url = qs
        ? `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}?${qs}`
        : `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}`;
      return await getApi<IntegrationBase[]>(url);
    } catch (error) {
      console.error("Erro ao buscar integrações base:", error);
      throw error;
    }
  }

  /**
   * Obtém uma integração base pelo ID
   * @param id ID da integração base
   * @returns Dados da integração base
   */
  static async getById(id: string): Promise<IntegrationBase> {
    try {
      // Sem join inline de template: quem precisa do template busca separado.
      return await getApi<IntegrationBase>(
        `${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}`,
      );
    } catch (error) {
      console.error(`Erro ao buscar integração base com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Cria uma nova integração base
   * @param data Dados da integração base a ser criada
   * @returns Integração base criada e, quando o template sinaliza via
   *          settings.post_install.emit_webhook, também um objeto post_install
   *          com a URL do webhook e as instruções de setup para exibição em modal.
   */
  static async create(
    data: CreateIntegrationBaseData,
  ): Promise<CreateIntegrationBaseResult> {
    try {
      const body = this.buildCreateBody(data);
      const result = await postApi<{
        integration: IntegrationBase;
        post_install?: IntegrationPostInstall;
      }>(`${API_CONFIG.API_ROOT_URL}${this.BASE_URL}`, body);
      return {
        integration: result.integration,
        post_install: result.post_install,
      };
    } catch (error) {
      console.error("Erro ao criar integração base:", error);
      throw error;
    }
  }

  /**
   * Atualiza uma integração base existente
   * @param id ID da integração base
   * @param data Dados a serem atualizados
   * @returns Integração base atualizada e, quando o template sinaliza via
   *          settings.post_install, também o objeto post_install com endpoints,
   *          manifest e instruções para exibição no modal pós-edição.
   */
  static async update(
    id: string,
    data: UpdateIntegrationBaseData,
  ): Promise<{
    integration: IntegrationBase;
    post_install?: IntegrationPostInstall;
  }> {
    try {
      const body = this.buildUpdateBody(data);
      const result = await putApi<{
        integration: IntegrationBase;
        post_install?: IntegrationPostInstall;
      }>(`${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}`, body);
      return {
        integration: result.integration,
        post_install: result.post_install,
      };
    } catch (error) {
      console.error(`Erro ao atualizar integração base com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Remove uma integração base
   * @param id ID da integração base
   * @returns Verdadeiro se a remoção foi bem-sucedida
   * @throws IntegrationDependencyError quando a integração possui dependências (apenas legacy; v2 deixa FK do Postgres falhar)
   */
  static async delete(id: string): Promise<void> {
    try {
      await delApi(`${API_CONFIG.API_ROOT_URL}${this.BASE_URL}/${id}`);
      return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error(`Erro ao remover integração base com ID ${id}:`, error);

      // Verificar se é um erro 400 com dependências (usar response.data se data estiver null)
      const errorData = error.data || error.response?.data;
      if (error.status === 400 && errorData?.dependencies) {
        throw new IntegrationDependencyError(errorData);
      }

      throw error;
    }
  }

  /**
   * Constrói body aceito pelo v2 `POST /integration-bases`.
   *
   * v2 valida: `integration_id`, `label`, `description?`, `status?`, `scope?`,
   * `product_id?`, `settings?`, `mcp_config?`, `credentials?` (record string→string).
   * Tudo o mais é derivado do template (`name`, `auth_type`, `auth_fields`,
   * `logo_url`, `url`, `classification`) ou da identidade autenticada
   * (`organization_id`, `created_by`). `title` não existe em v2.
   */
  private static buildCreateBody(data: CreateIntegrationBaseData) {
    // `product_id` deriva scope 'local' no v2 e cria o vínculo em
    // integrations_product (parity legacy). Sem ele, scope explícito do caller
    // (default v2 = 'global').
    return {
      integration_id: data.integration_id,
      label: data.label,
      ...(data.description !== undefined
        ? { description: data.description }
        : {}),
      ...(data.status !== undefined ? { status: data.status } : {}),
      ...(data.scope !== undefined ? { scope: data.scope } : {}),
      ...(data.product_id !== undefined ? { product_id: data.product_id } : {}),
      ...(data.settings !== undefined ? { settings: data.settings } : {}),
      ...(data.mcp_config !== undefined ? { mcp_config: data.mcp_config } : {}),
      ...(data.credentials !== undefined
        ? { credentials: this.coerceCredentials(data.credentials) }
        : {}),
    };
  }

  /**
   * Constrói body aceito pelo v2 `PUT /integration-bases/:id`. Mesma forma do
   * create sem `integration_id` (não muta após criação). `scope` + `product_id`
   * juntos gerenciam o vínculo de produto na mudança de escopo (parity legacy).
   */
  private static buildUpdateBody(data: UpdateIntegrationBaseData) {
    return {
      ...(data.label !== undefined ? { label: data.label } : {}),
      ...(data.description !== undefined
        ? { description: data.description }
        : {}),
      ...(data.status !== undefined ? { status: data.status } : {}),
      ...(data.scope !== undefined ? { scope: data.scope } : {}),
      ...(data.product_id !== undefined ? { product_id: data.product_id } : {}),
      ...(data.settings !== undefined ? { settings: data.settings } : {}),
      ...(data.mcp_config !== undefined ? { mcp_config: data.mcp_config } : {}),
      ...(data.credentials !== undefined
        ? { credentials: this.coerceCredentials(data.credentials) }
        : {}),
    };
  }

  /**
   * v2 valida `credentials` como `Record<string, string>`. Coerce valores não-string
   * preservando keys; descarta undefined/null.
   */
  private static coerceCredentials(
    creds: Record<string, unknown>,
  ): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(creds)) {
      if (value === undefined || value === null) continue;
      out[key] = typeof value === "string" ? value : String(value);
    }
    return out;
  }

  /**
   * Obtém integrações base ativas — organização derivada do token.
   * @param workspaceId Se informado, aplica escopo: retorna globais + locais vinculadas ao produto
   * @returns Lista de integrações base ativas
   */
  static async getActive(workspaceId?: string): Promise<IntegrationBase[]> {
    return this.getAll({
      status: "active",
      ...(workspaceId ? { product_id: workspaceId } : {}),
    });
  }
}
